import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, KeyRound, RotateCcw, TriangleAlert } from 'lucide-react'
import AuthField from '../auth/AuthField'
import AuthHead from '../auth/AuthHead'
import AuthLayout from '../auth/AuthLayout'
import Button from '../../components/Button'
import LoadingOverlay from '../../components/LoadingOverlay'
import ResultDialog from '../../components/ResultDialog'
import { checkOtp, clearPendingOtpUser, getPendingOtpUser, goToLanding, resendOtp } from './loginService'
import { strings as S } from './strings'
import './OtpPage.css'

/**
 * The one-time password step. In the legacy app this is a modal over the Login
 * form; the content, actions and messages are the same, only the surface differs.
 */
export default function OtpPage() {
  const navigate = useNavigate()

  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)

  const pendingUser = getPendingOtpUser()

  // No half-finished login in the session means this route was opened directly.
  useEffect(() => {
    if (!pendingUser) navigate('/login', { replace: true })
  }, [pendingUser, navigate])

  if (!pendingUser) return null

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    setSubmitting(true)
    const data = await checkOtp(otp)
    setSubmitting(false)

    if (!data.status) {
      setError(data.message)
      return
    }

    /* The cookie is set. "Remember me" was already persisted on the login step, and
       where this user belongs is the server's call — see goToLanding(). */
    clearPendingOtpUser()
    goToLanding()
  }

  async function handleResend() {
    setError('')
    setBusy(true)
    const data = await resendOtp()
    setBusy(false)
    setResult({ result: data.Result, message: data.Message })
  }

  return (
    <AuthLayout>
      <form className="auth__card" onSubmit={handleSubmit} noValidate>
        <AuthHead
          title={S.otp}
          subtitle={pendingUser}
          mark={
            <span className="otp__badge" aria-hidden="true">
              <KeyRound size={20} />
            </span>
          }
        />

        <AuthField
          id="txtOTP"
          label={S.otp}
          value={otp}
          onChange={setOtp}
          invalid={Boolean(error)}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          className="otp__input"
        />

        <Button type="submit" variant="primary" className="auth__submit" disabled={submitting}>
          {submitting ? S.submitting : S.backToLogin}
          {!submitting && <ArrowLeft className="ltr-flip" size={17} aria-hidden="true" />}
        </Button>

        {error && (
          <p className="auth__error" role="alert">
            <TriangleAlert size={16} aria-hidden="true" />
            <bdi>{error}</bdi>
          </p>
        )}

        <footer className="auth__foot">
          <button type="button" className="auth__link" onClick={handleResend} disabled={busy}>
            <RotateCcw size={14} aria-hidden="true" />
            {S.resendOtp}
          </button>
        </footer>
      </form>

      <LoadingOverlay open={busy} label={S.waitScreen} />
      <ResultDialog result={result} closeLabel={S.close} onClose={() => setResult(null)} />
    </AuthLayout>
  )
}
